import { useRef, useState, useId } from 'react';
import cx from 'classnames';
import { ClickAwayListener, Grow, ListItem, MenuItem, Paper, Popper } from '@mui/material';
import SeeMore from '@classify/react-core-components/SeeMore';
import { styled } from '@classify/react-core-components/styles';
import TextField from '../../../forms/components/TextField';
import MenuItemName from './MenuItemName';
import MenuPaginatedList from './MenuPaginatedList';
const PREFIX = 'SelectFieldSearch';
const classes = {
  root: `${PREFIX}-root`,
  textFieldRoot: `${PREFIX}-textFieldRoot`,
  formControl: `${PREFIX}-formControl`,
  fieldContainer: `${PREFIX}-fieldContainer`,
  paper: `${PREFIX}-paper`,
  popper: `${PREFIX}-popper`
};
const Root = styled('div')(/* istanbul ignore next */({
  theme: {
    zIndex
  }
}) => {
  const fieldHeight = 49;
  return {
    [`& .${classes.textFieldRoot}`]: {
      '&& label': {
        fontSize: 14,
        transform: 'translate(8px, 8px) scale(0.75)'
      }
    },
    [`& .${classes.formControl}`]: {
      '&&': {
        width: '100%',
        maxWidth: '100vw'
      }
    },
    [`& .${classes.fieldContainer}`]: {
      minHeight: fieldHeight
    },
    [`& .${classes.paper}`]: {
      margin: 0,
      position: 'relative',
      zIndex: zIndex.modal + 1
    },
    [`& .${classes.popper}`]: {
      zIndex: zIndex.modal + 1,
      '&[x-placement="bottom"] .SelectFieldSearch-paper': {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0
      },
      '&[x-placement="top"] .SelectFieldSearch-paper': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0
      }
    }
  };
});
function SelectFieldSearch({
  items = [],
  name,
  isLoading = false,
  onSeeMoreClick = () => {},
  onChange = () => {},
  onSelected = () => {},
  totalEntities = null,
  translations = {
    label: 'Label',
    totalResults: 'X on Y',
    seeMore: 'See more',
    noResults: '"XXX" not found',
    menu: 'Menu',
    loading: 'Loading',
    createButton: 'Create',
    placeholder: 'Placeholder'
  },
  createUrl = '',
  error = '',
  value = '',
  onPaste = () => {},
  ...props
}) {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const autocompleteUID = useId();
  const selectFieldSearchUID = `selectFieldSearch-${autocompleteUID}`;
  const fieldRef = useRef(null);
  const closeMenu = /* istanbul ignore next */() => {
    setIsMenuOpened(false);
  };
  const handleClickAway = /* istanbul ignore next */() => {
    if (!isFocus) {
      closeMenu();
    }
  };
  const shouldRenderSeeMoreItem = Boolean(items.length) && items.length < totalEntities;
  const onItemClick = item => {
    closeMenu();
    onSelected({
      ...item,
      value: item.name
    });
  };
  const shouldOpenMenu = Boolean(fieldRef.current) && isMenuOpened;
  const closeMenuOnTab = /* istanbul ignore next */event => {
    if (shouldRenderSeeMoreItem) {
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      closeMenu();
    }
  };
  const shouldShowMenuList = isLoading && items.length === 0;
  return <Root className={classes.root}>
      <div className={cx(classes.formControl, classes.fieldContainer)}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <TextField ref={fieldRef} name={name} error={error} label={translations.placeholder} onChange={e => onChange(e.target.value)} onFocus={() => {
            setIsMenuOpened(true);
            setIsFocus(true);
          }} onBlur={() => {
            setIsFocus(false);
          }} onKeyDown={isLoading ? closeMenuOnTab : null} aria-haspopup="true" aria-controls={shouldOpenMenu ? selectFieldSearchUID : undefined} inputProps={{
            autoComplete: 'off'
          }} value={value} classes={value ? {
            root: classes.textFieldRoot
          } : {}} onPaste={event_0 => {
            onPaste(event_0, () => setIsMenuOpened(false));
          }} {...props} />
            <Popper anchorEl={fieldRef.current} open={shouldOpenMenu} className={classes.popper} role={undefined} transition disablePortal>
              {({
              TransitionProps,
              placement
            }) => <Grow {...TransitionProps} style={{
              transformOrigin: /* istanbul ignore next */placement === 'bottom' ? 'center top' : 'center bottom'
            }}>
                  <Paper className={classes.paper} elevation={15} style={{
                width: fieldRef.current?.clientWidth || 0
              }}>
                    <MenuPaginatedList id={selectFieldSearchUID} onKeyDown={closeMenuOnTab} isLoading={shouldShowMenuList} query={fieldRef.current.value} createUrl={createUrl} translations={translations}>
                      {items.map(item_0 => <MenuItem key={item_0.id} onClick={() => onItemClick(item_0)} aria-label={translations.menu}>
                          <MenuItemName name={item_0.name} />
                        </MenuItem>)}

                      {shouldRenderSeeMoreItem && <SeeMore rootComponent={ListItem} isLoading={isLoading} onClick={onSeeMoreClick} shouldShowResultCount={!isLoading} translations={{
                    seeMore: translations.seeMore,
                    totalResults: translations.totalResults
                  }} buttonProps={{
                    onKeyDown: closeMenuOnTab
                  }} />}
                    </MenuPaginatedList>
                  </Paper>
                </Grow>}
            </Popper>
          </div>
        </ClickAwayListener>
      </div>
    </Root>;
}
export default SelectFieldSearch;